import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Lightbulb, BookOpen, Check, X, ChevronRight, Star, RotateCcw, Volume2, VolumeX, Loader2 } from 'lucide-react';

const LessonPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { api, updateUser, user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adaptiveDifficulty, setAdaptiveDifficulty] = useState('easy');
  
  // Audio state
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  
  // Tab state for lesson sections
  const [activeTab, setActiveTab] = useState('learn');
  
  // Quiz state
  const [currentExercise, setCurrentExercise] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [showLesson, setShowLesson] = useState(true);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        // Fetch lesson content
        const response = await api.get(`/v2/curriculum/lesson/${lessonId}`);
        let lessonData = response.data;
        
        // Fetch adaptive difficulty recommendation
        try {
          const topicId = lessonId.split('-lesson-')[0];
          const subject = lessonData?.subject || 'maths';
          const adaptiveRes = await api.get(`/adaptive/mastery`);
          
          // Find mastery for this topic
          const topicMastery = adaptiveRes.data?.by_subject?.[subject]?.topics?.find(
            t => t.topic_id === topicId
          );
          
          if (topicMastery) {
            const recommendedDiff = topicMastery.recommended_difficulty || 'easy';
            setAdaptiveDifficulty(recommendedDiff);
            
            // Filter exercises based on adaptive difficulty
            if (lessonData.exercises && lessonData.exercises.length > 0) {
              const filteredExercises = filterExercisesByDifficulty(
                lessonData.exercises, 
                recommendedDiff
              );
              lessonData = { ...lessonData, exercises: filteredExercises, adaptive_difficulty: recommendedDiff };
            }
          }
        } catch (adaptiveError) {
          console.log('Adaptive difficulty not available, using default');
        }
        
        setLesson(lessonData);
      } catch (error) {
        console.error('Error fetching lesson:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLesson();
  }, [lessonId, api]);

  // Filter exercises based on mastery-recommended difficulty
  const filterExercisesByDifficulty = (exercises, recommendedDifficulty) => {
    // If exercises have difficulty tags, prioritize appropriate ones
    const hasTaggedExercises = exercises.some(e => e.difficulty);
    
    if (!hasTaggedExercises) {
      // No difficulty tags - return all exercises
      return exercises;
    }
    
    // Get exercises matching or below recommended difficulty
    const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
    const targetLevel = difficultyOrder[recommendedDifficulty] || 2;
    
    // Prioritize exercises at or near recommended difficulty
    const prioritized = exercises.filter(e => {
      const exLevel = difficultyOrder[e.difficulty] || 2;
      // Include exercises at target level or one level below/above
      return Math.abs(exLevel - targetLevel) <= 1;
    });
    
    // If we have enough exercises at appropriate difficulty, use them
    if (prioritized.length >= 3) {
      return prioritized;
    }
    
    // Otherwise return all exercises
    return exercises;
  };

  // Show listen button for all students (especially helpful for younger ones)
  const showAudioButton = true;

  const handleListenToLesson = async () => {
    if (audioLoading) return;
    
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    
    try {
      setAudioLoading(true);
      
      // Get audio from v2 media API
      const response = await api.post(`/v2/media/tts/lesson/${lessonId}`);
      
      if (response.data?.audio) {
        // Audio is base64 encoded
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.play();
          setIsPlaying(true);
        }
      } else {
        throw new Error('No audio data received');
      }
      
    } catch (error) {
      console.error('Error generating audio:', error);
      if (error.response?.status === 402) {
        toast.error('Audio generation limit reached. Please try again later.');
      } else {
        toast.error('Could not generate audio. Please try again.');
      }
    } finally {
      setAudioLoading(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleStartExercises = () => {
    setShowLesson(false);
    // Stop audio when starting exercises
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (showFeedback) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) return;
    
    const correct = selectedAnswer === lesson.exercises[currentExercise].correct_answer;
    setIsCorrect(correct);
    setShowFeedback(true);
    
    if (correct) {
      setScore(prev => prev + 1);
      toast.success('Correct! Well done!', { duration: 1500 });
    } else {
      toast.error('Not quite right!', { duration: 1500 });
    }
    
    // Record attempt for adaptive learning
    try {
      const topicId = lessonId.split('-lesson-')[0];
      await api.post('/adaptive/record-attempt', {
        topic_id: topicId,
        subject: lesson?.subject || 'unknown',
        key_stage: lesson?.key_stage || user?.key_stage || 'ks2',
        is_correct: correct,
        difficulty: lesson?.recommended_difficulty || 'medium'
      });
    } catch (error) {
      // Silently fail - don't interrupt the learning flow
      console.log('Adaptive tracking:', error.message);
    }
  };

  const [saving, setSaving] = useState(false);

  const persistProgress = async (payload, attempt = 1) => {
    try {
      const response = await api.post('/progress', payload);
      // Clear any queued retry for this lesson
      try { localStorage.removeItem(`pending_progress_${payload.lesson_id}`); } catch (_) {}
      return { ok: true, response };
    } catch (error) {
      console.error(`Progress save failed (attempt ${attempt}):`, error);
      if (attempt < 3) {
        await new Promise(r => setTimeout(r, attempt * 500));
        return persistProgress(payload, attempt + 1);
      }
      // After retries failed — queue locally for later retry
      try {
        localStorage.setItem(`pending_progress_${payload.lesson_id}`, JSON.stringify({
          payload, queued_at: new Date().toISOString()
        }));
      } catch (_) {}
      return { ok: false, error };
    }
  };

  const handleNext = async () => {
    if (currentExercise < lesson.exercises.length - 1) {
      setCurrentExercise(prev => prev + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setIsCorrect(false);
    } else {
      // Complete lesson - score is already fully counted from handleSubmitAnswer
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const finalScore = score;
      const topicId = lessonId.split('-lesson-')[0];
      const payload = {
        topic_id: topicId,
        lesson_id: lessonId,
        score: finalScore,
        total_questions: lesson.exercises.length,
        time_spent_seconds: timeSpent,
        difficulty: lesson.adaptive_difficulty || lesson.recommended_difficulty || 'easy'
      };

      setSaving(true);
      const result = await persistProgress(payload);
      setSaving(false);

      if (result.ok) {
        if (result.response?.data?.user_stats) {
          updateUser({
            total_points: result.response.data.user_stats.total_points,
            total_stars: result.response.data.user_stats.total_stars
          });
        }
        toast.success('Lesson completed!', { duration: 1800 });
        setCompleted(true);
      } else {
        toast.error('Could not save your progress. Tap to retry.', {
          duration: 8000,
          action: {
            label: 'Retry',
            onClick: async () => {
              setSaving(true);
              const r2 = await persistProgress(payload);
              setSaving(false);
              if (r2.ok) {
                toast.success('Saved!');
                setCompleted(true);
              } else {
                toast.error('Still offline. Your progress is queued and will save when you reconnect.');
              }
            }
          }
        });
        // Do NOT mark complete — lesson must save before we hide it from the daily chart
      }
    }
  };

  const calculateStars = () => {
    // Score already includes all answered questions
    const percentage = (score / lesson.exercises.length) * 100;
    if (percentage >= 90) return 3;
    if (percentage >= 70) return 2;
    if (percentage >= 50) return 1;
    return 0;
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </StudentLayout>
    );
  }

  if (!lesson) {
    return (
      <StudentLayout>
        <div className="text-center py-12">
          <h2 className="font-heading text-2xl font-bold text-slate-800">Lesson not found</h2>
        </div>
      </StudentLayout>
    );
  }

  // Completed screen
  if (completed) {
    const stars = calculateStars();
    const percentage = Math.round((score / lesson.exercises.length) * 100);
    
    return (
      <StudentLayout>
        <div className="max-w-2xl mx-auto">
          <div className="student-card text-center">
            <div className="text-6xl mb-4">
              {stars === 3 ? '🎉' : stars >= 1 ? '👏' : '💪'}
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-800 mb-2">
              {stars === 3 ? 'Perfect!' : stars >= 1 ? 'Great Job!' : 'Good Try!'}
            </h1>
            <p className="text-slate-600 text-lg mb-6">
              You got {score} out of {lesson.exercises.length} correct!
            </p>
            
            <div className="bg-purple-50 rounded-2xl border-2 border-purple-200 p-6 mb-6">
              <div className="text-5xl font-heading font-bold text-purple-600 mb-2">{percentage}%</div>
              <div className="flex justify-center gap-2 mb-2">
                {[1, 2, 3].map(s => (
                  <Star 
                    key={s}
                    className={`w-10 h-10 ${s <= stars ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <p className="text-purple-700 font-semibold">You earned {stars} stars!</p>
            </div>

            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => {
                  setCurrentExercise(0);
                  setSelectedAnswer(null);
                  setShowFeedback(false);
                  setIsCorrect(false);
                  setScore(0);
                  setCompleted(false);
                  setShowLesson(true);
                }}
                variant="outline"
                className="font-bold rounded-xl px-6 py-3 border-2"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/student')}
                className="student-btn"
              >
                Continue
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </StudentLayout>
    );
  }

  // Lesson content screen
  if (showLesson) {
    return (
      <StudentLayout>
        <div className="max-w-3xl mx-auto">
          {/* Hidden audio element */}
          <audio ref={audioRef} onEnded={handleAudioEnded} style={{ display: 'none' }} />
          
          <Link 
            to="/student"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-purple-600 font-semibold mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>

          <div className="student-card mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">{lesson.title}</h1>
              </div>
              
              {/* Listen Button for younger students */}
              {showAudioButton && (
                <Button
                  onClick={handleListenToLesson}
                  variant="outline"
                  className={`flex items-center gap-2 ${isPlaying ? 'bg-purple-100 border-purple-400 text-purple-700' : ''}`}
                  disabled={audioLoading}
                  data-testid="listen-button"
                >
                  {audioLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isPlaying ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                  {audioLoading ? 'Loading...' : isPlaying ? 'Stop' : 'Listen'}
                </Button>
              )}
            </div>
          </div>

          {/* Section Tabs */}
          <div className="student-card mb-6">
            <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-4">
              <button
                onClick={() => setActiveTab('learn')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === 'learn' 
                    ? 'bg-purple-600 text-white shadow-md' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="tab-learn"
              >
                <BookOpen className="w-4 h-4" />
                Learn
              </button>
              
              {lesson.key_vocabulary && lesson.key_vocabulary.length > 0 && (
                <button
                  onClick={() => setActiveTab('vocabulary')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    activeTab === 'vocabulary' 
                      ? 'bg-emerald-600 text-white shadow-md' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-vocabulary"
                >
                  <span className="text-sm">📚</span>
                  Vocabulary
                </button>
              )}
              
              {lesson.tips && lesson.tips.length > 0 && (
                <button
                  onClick={() => setActiveTab('tips')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    activeTab === 'tips' 
                      ? 'bg-amber-500 text-white shadow-md' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-tips"
                >
                  <Lightbulb className="w-4 h-4" />
                  Top Tips
                </button>
              )}
            </div>

            {/* Learn Tab Content */}
            {activeTab === 'learn' && (
              <div className="animate-in fade-in duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-slate-800">Learn</h2>
                </div>
                
                {/* Learning Objectives */}
                {lesson.objectives && lesson.objectives.length > 0 && (
                  <div className="mb-6 bg-blue-50 rounded-xl p-4">
                    <h3 className="font-semibold text-blue-800 mb-2">Learning Objectives:</h3>
                    <ul className="space-y-1">
                      {lesson.objectives.map((obj, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-blue-700">
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{obj}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Main Teaching Content */}
                {lesson.content ? (
                  <div className="prose prose-slate max-w-none">
                    <div 
                      className="text-slate-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: lesson.content
                          .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-slate-800 mt-6 mb-3">$1</h2>')
                          .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-slate-700 mt-4 mb-2">$1</h3>')
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-purple-700">$1</strong>')
                          .replace(/\n\n/g, '</p><p class="mb-4">')
                          .replace(/```([^`]+)```/g, '<pre class="bg-slate-100 p-4 rounded-lg overflow-x-auto my-4 text-sm font-mono">$1</pre>')
                          .replace(/\|(.+)\|/g, (match) => {
                            return '<div class="overflow-x-auto my-4"><table class="min-w-full border-collapse border border-slate-300 text-sm">' + 
                              match.split('\n').filter(row => row.trim() && !row.includes('---')).map((row, idx) => {
                                const cells = row.split('|').filter(cell => cell.trim());
                                const cellTag = idx === 0 ? 'th' : 'td';
                                const cellClass = idx === 0 ? 'bg-purple-100 font-semibold' : '';
                                return '<tr>' + cells.map(cell => `<${cellTag} class="border border-slate-300 px-3 py-2 ${cellClass}">${cell.trim()}</${cellTag}>`).join('') + '</tr>';
                              }).join('') + '</table></div>';
                          })
                      }} 
                    />
                  </div>
                ) : (
                  <p className="text-slate-700 text-lg">{lesson.explanation?.content || 'Get ready to learn and practice!'}</p>
                )}
              </div>
            )}

            {/* Vocabulary Tab Content */}
            {activeTab === 'vocabulary' && lesson.key_vocabulary && lesson.key_vocabulary.length > 0 && (
              <div className="animate-in fade-in duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <span className="text-lg">📚</span>
                  </div>
                  <h2 className="font-heading text-xl font-bold text-slate-800">Key Vocabulary</h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {lesson.key_vocabulary.map((word, idx) => (
                    <div key={idx} className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 hover:shadow-md transition-shadow">
                      <span className="text-emerald-800 font-semibold text-lg">{word}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tips Tab Content */}
            {activeTab === 'tips' && lesson.tips && lesson.tips.length > 0 && (
              <div className="animate-in fade-in duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-amber-800">Top Tips</h2>
                </div>
                <div className="space-y-3">
                  {lesson.tips.map((tip, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <span className="text-2xl">💡</span>
                      <span className="text-amber-900 text-lg">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Example Section - only show if available */}
          {lesson.explanation?.example && (
            <div className="student-card bg-amber-50 border-amber-300 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">📝</span>
                <h2 className="font-heading text-xl font-bold text-slate-800">Example</h2>
              </div>
              <p className="text-amber-900 text-lg font-medium">{lesson.explanation?.example}</p>
            </div>
          )}

          <div className="text-center">
            <Button onClick={handleStartExercises} className="student-btn text-lg px-10 py-6">
              Start Exercises
              <ChevronRight className="w-6 h-6 ml-2" />
            </Button>
          </div>
        </div>
      </StudentLayout>
    );
  }

  // Exercise screen
  const exercise = lesson.exercises[currentExercise];

  return (
    <StudentLayout>
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-slate-600">
              Question {currentExercise + 1} of {lesson.exercises.length}
            </span>
            <span className="font-bold text-purple-600">Score: {score}</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
            <div 
              className="h-full bg-purple-600 rounded-full transition-all"
              style={{ width: `${((currentExercise + (showFeedback ? 1 : 0)) / lesson.exercises.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="student-card mb-6">
          <h2 className="font-heading text-xl font-bold text-slate-800 mb-6">{exercise.question}</h2>
          
          <div className="space-y-3">
            {exercise.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrectAnswer = index === exercise.correct_answer;
              
              let buttonClass = 'w-full p-4 rounded-xl border-2 text-left font-semibold transition-all ';
              
              if (showFeedback) {
                if (isCorrectAnswer) {
                  buttonClass += 'bg-green-100 border-green-500 text-green-800';
                } else if (isSelected && !isCorrectAnswer) {
                  buttonClass += 'bg-red-100 border-red-500 text-red-800';
                } else {
                  buttonClass += 'bg-gray-50 border-gray-200 text-gray-400';
                }
              } else {
                if (isSelected) {
                  buttonClass += 'bg-purple-100 border-purple-500 text-purple-800';
                } else {
                  buttonClass += 'bg-white border-gray-300 hover:border-purple-300 text-slate-700';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showFeedback}
                  className={buttonClass}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                      showFeedback && isCorrectAnswer ? 'bg-green-500 text-white' :
                      showFeedback && isSelected && !isCorrectAnswer ? 'bg-red-500 text-white' :
                      isSelected ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {showFeedback && isCorrectAnswer ? <Check className="w-5 h-5" /> :
                       showFeedback && isSelected && !isCorrectAnswer ? <X className="w-5 h-5" /> :
                       String.fromCharCode(65 + index)}
                    </span>
                    {option}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feedback */}
        {showFeedback && (
          <div className={`student-card mb-6 ${isCorrect ? 'bg-green-50 border-green-400' : 'bg-amber-50 border-amber-400'}`}>
            <div className="flex items-center gap-3 mb-2">
              {isCorrect ? (
                <>
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-heading text-lg font-bold text-green-800">Correct!</span>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                    <X className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-heading text-lg font-bold text-amber-800">Not quite!</span>
                </>
              )}
            </div>
            <p className={isCorrect ? 'text-green-700' : 'text-amber-700'}>
              {exercise.explanation}
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="text-center">
          {!showFeedback ? (
            <Button
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
              className="student-btn text-lg px-10 py-6 disabled:opacity-50"
            >
              Check Answer
            </Button>
          ) : (
            <Button onClick={handleNext} className="student-btn-secondary text-lg px-10 py-6">
              {currentExercise < lesson.exercises.length - 1 ? 'Next Question' : 'See Results'}
              <ChevronRight className="w-6 h-6 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default LessonPage;
